import { FetchResult } from "@apollo/client";
import { isAfter, isBefore, isFuture, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useRouter } from "next/router";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import { VerifyClient2FaCode } from "@/__generated__/schema.graphql.types";
import { trackMetaPixelEvent } from "@/components/booking/metaPixel";
import { useAnalytics } from "@/components/common/segment/AnalyticsContext";
import { useConfirm } from "@/components/providers/confirmProvider";
import {
  CreateAppointmentByClientMutation,
  useCreateAppointmentByClientMutation,
} from "@/graphql/mutations/createAppointmentByClient.graphql.types";
import { SelectiveBookingLinkQueryResult } from "@/graphql/queries/onlineBooking/selectiveBookingLinks.graphql.types";
import { useSelectiveEffect } from "@/hooks/misc/useSelectiveEffect";
import { AppointmentType } from "@/types";
import {
  BookingStep,
  ClientDetailsInput,
  TimeSlot,
} from "@/types/bookingTypes";
import { calculateBookingCustomizationEnabled } from "@/utils";
import {
  ClientBookingErrors,
  DefaultBookingState,
  DEPOSIT_ERRORS,
  getEligibleProviders,
} from "@/utils/booking";
import { hasDjangoMutationError } from "@/utils/djangoMutationError";
import { FeatureFlags } from "@/utils/featureFlags";
import { getCurrentTimeInTimezone } from "@/utils/getBookingRangeDates";
import { Datadog } from "@/utils/observability/datadog";
import useErrorLogger from "@/utils/useErrorLogger";
import useWarningLogger from "@/utils/useWarningLogger";
import { MedspaInfo } from "@/views/booking/bookingView";
import { MedspaOnlineStoreData } from "@/views/online-store/onlineStoreView";

type ClientBookingContextType = ReturnType<typeof useClientBookingContextValue>;

const ClientBookingContext = createContext<ClientBookingContextType>(null);

const useClientBookingContextValue = (
  medspaData: MedspaInfo,
  defaultState: DefaultBookingState,
  packagesInfo: MedspaOnlineStoreData["packages"] | null,
  selectiveBookingLinkData: SelectiveBookingLinkQueryResult["data"] | null
) => {
  const router = useRouter();
  const selectiveBookingLink =
    selectiveBookingLinkData?.selectiveBookingLinkByPk;
  const analytics = useAnalytics();
  const logError = useErrorLogger();
  const logWarning = useWarningLogger();
  const { getConfirm } = useConfirm();

  // STATE used for BOOKING
  const [step, setStep] = useState<BookingStep>(defaultState.step);
  const [selectedServices, setSelectedServices] = useState<
    MedspaInfo["serviceMenuItems"]
  >(defaultState.selectedServices);
  const [selectedProvider, setSelectedProvider] = useState<
    MedspaInfo["userMedspas"][number]
  >(defaultState.selectedProvider);
  const [timeSlot, setTimeSlot] = useState<TimeSlot>(defaultState.timeSlot);

  // In order to use date from defaultState, we need to check if it's in the future, otherwise use current date
  // This prevents providers copying links with dates and confusing clients
  const [date, setDate] = useState<Date>(
    defaultState.date && isFuture(parseISO(defaultState.date))
      ? utcToZonedTime(parseISO(defaultState.date), medspaData.timezone)
      : getCurrentTimeInTimezone(medspaData.timezone)
  );

  const [clientDetails, setClientDetails] = useState<ClientDetailsInput>(
    defaultState.clientDetails
  );
  const [card, setCard] = useState<
    VerifyClient2FaCode["clientData"]["card"] | null
  >(null);
  const [code, setCode] = useState("");
  const [clientAccessToken, setClientAccessToken] = useState("");
  const [codeExpired, setCodeExpired] = useState(false);
  const [depositPaymentError, setDepositPaymentError] = useState("");
  const [accountCreationError, setAccountCreationError] = useState("");
  const [isReturningClient, setIsReturningClient] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>();
  const [creditCardVerified, setCreditCardVerified] = useState(false);
  const [visitId, setVisitId] = useState<string>(null);
  const [bookAppointment] = useCreateAppointmentByClientMutation();

  const isSelectiveBookingLink = !!router.query.selectiveBookingLinkId;

  useSelectiveEffect(() => {
    analytics.track(
      "Public Booking Step Started",
      { step: step },
      {
        context: {
          groupId: medspaData.id,
        },
      }
    );
  }, [step]);
  useEffect(() => {
    Datadog.setMedspaId(medspaData.id);
    return Datadog.clearMedspaId;
  }, [medspaData.id]);

  // COMMON STATE useful for multiple steps
  const providers = useMemo(() => {
    let filteredProviders = medspaData.userMedspas;

    if (isSelectiveBookingLink && selectiveBookingLink?.providers?.length) {
      filteredProviders = filteredProviders.filter((provider) =>
        selectiveBookingLink.providers.some((p) => p.id === provider.user.id)
      );
    }

    return filteredProviders;
  }, [medspaData.userMedspas, isSelectiveBookingLink, selectiveBookingLink]);

  const eligibleProviders = useMemo(() => {
    const eligibleProviders = getEligibleProviders(selectedServices, providers);
    const shouldSortByUserMedspaId =
      !medspaData.configuration.providerDisplayOrderEnabled;
    if (shouldSortByUserMedspaId) {
      return eligibleProviders.sort((a, b) => +a.id - +b.id);
    }
    return eligibleProviders;
  }, [
    selectedServices,
    providers,
    medspaData.configuration.providerDisplayOrderEnabled,
  ]);
  const showServicePriceOnBooking =
    medspaData.configuration.showServicePriceOnBooking;

  const hasMixedAppointmentTypes =
    selectedServices.some(
      (service) => service.appointmentType === AppointmentType.IN_PERSON
    ) &&
    selectedServices.some(
      (service) => service.appointmentType === AppointmentType.TELEHEALTH
    );

  const onlineBookingDepositsV1Enabled =
    medspaData.additionalPublicInfo.enabledFeatures.includes(
      FeatureFlags.ONLINE_BOOKING_DEPOSITS_V1
    );

  const { cancellationFeePolicyEnabled, allowDeposits } =
    medspaData.configuration;
  const depositsEnabled = onlineBookingDepositsV1Enabled && allowDeposits;
  const collectCardInformation =
    cancellationFeePolicyEnabled || depositsEnabled;

  const handleBookingSubmit = async (data?: ClientDetailsInput) => {
    const clientData = data || clientDetails;
    const { firstName, lastName, email, phone, comment: note } = clientData;

    try {
      const startTime = new Date(timeSlot.startStr);
      const endTime = new Date(timeSlot.endStr);

      const isNotEligibleForSelectiveBooking = !isEligibleForSelectiveBooking({
        selectedAppointmentStartTime: startTime,
        selectedAppointmentEndTime: endTime,
        isSelectiveBookingLink,
      });

      if (depositPaymentError) setDepositPaymentError("");

      if (isNotEligibleForSelectiveBooking) {
        const shouldContinue = await handleIneligibleSelectiveBooking();
        if (!shouldContinue) return;
      }

      const result = await createAppointment({
        firstName,
        lastName,
        email,
        phone,
        note,
        startTime,
        endTime,
        selectiveBookingId: selectiveBookingLink?.id,
      });

      handleSuccessfulBooking(result);
      return result;
    } catch (e) {
      handleBookingError(e);
    }
  };

  const isEligibleForSelectiveBooking = ({
    selectedAppointmentStartTime,
    selectedAppointmentEndTime,
    isSelectiveBookingLink,
  }: {
    selectedAppointmentStartTime: Date;
    selectedAppointmentEndTime: Date;
    isSelectiveBookingLink: boolean;
  }) => {
    // If not a selective booking link, everything is eligible
    if (!isSelectiveBookingLink) return true;

    // Check all three conditions for selective booking eligibility
    const isTimeWindowValid = checkTimeWindowValidity(
      selectedAppointmentStartTime,
      selectedAppointmentEndTime
    );
    const isProviderValid = checkProviderValidity();
    const areServicesValid = checkServicesValidity();

    return isTimeWindowValid && isProviderValid && areServicesValid;
  };

  const checkTimeWindowValidity = (
    appointmentStart: Date,
    appointmentEnd: Date
  ) => {
    // If start date exists, check if appointment starts after it
    const isAfterStart = selectiveBookingLink.startDate
      ? isAfter(appointmentStart, parseISO(selectiveBookingLink.startDate))
      : true;

    // If end date exists, check if appointment ends before it
    const isBeforeEnd = selectiveBookingLink.endDate
      ? isBefore(appointmentEnd, parseISO(selectiveBookingLink.endDate))
      : true;

    // Appointment must fall within the link's time window (if specified)
    return isAfterStart && isBeforeEnd;
  };

  const checkProviderValidity = () => {
    return selectiveBookingLink.providers.some(
      (p) => p.id === selectedProvider.user.id
    );
  };

  const checkServicesValidity = () => {
    return selectedServices.every((service) =>
      selectiveBookingLink.services.some((s) => s.id === service.id)
    );
  };

  const handleIneligibleSelectiveBooking = async () => {
    const userResponse = await getConfirm({
      title: "Booking Outside Special Offer",
      description:
        "This appointment doesn't qualify for the special offer pricing. Would you like to continue booking at regular rates?",
      confirmButtonText: "Continue at Regular Rate",
      discardButtonText: "Cancel",
    });

    if (userResponse) {
      logWarning("Booking attempted outside custom booking criteria", {
        medspaId: medspaData.id,
        selectedProviderId: selectedProvider.id,
        selectedServices: selectedServices.map(({ id }) => id),
        selectiveBookingLink,
      });
    }

    return userResponse;
  };

  const createAppointment = async ({
    firstName,
    lastName,
    email,
    phone,
    note,
    startTime,
    endTime,
    selectiveBookingId,
  }) => {
    return await bookAppointment({
      variables: {
        firstName,
        lastName,
        email,
        phone,
        note: note || "",
        providerId: selectedProvider.user.id,
        services: selectedServices.map(({ id }) => id),
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        isFds: defaultState.isFds,
        selectiveBookingId,
      },
    });
  };

  const handleSuccessfulBooking = (
    result: FetchResult<CreateAppointmentByClientMutation>
  ) => {
    // Facebook/Meta Ads Conversion Tracking
    trackMetaPixelEvent("Schedule");

    // Segment Analytics Tracking
    analytics.track(
      "Appointment Scheduled",
      {
        location: "Public Booking Page",
        selectiveBookingId:
          result.data?.createSchedulingAppointmentByClient?.selectiveBookingId,
        appointmentId:
          result.data?.createSchedulingAppointmentByClient?.scheduledAppointment
            ?.id,
        visitId: result.data?.createSchedulingAppointmentByClient?.visitId,
      },
      {
        context: {
          groupId: medspaData.id,
        },
      }
    );

    setStep(BookingStep.BOOKED);
    setVisitId(result.data.createSchedulingAppointmentByClient.visitId);
    setClientAccessToken(
      result.data.createSchedulingAppointmentByClient.clientAccessToken
    );
  };

  const handleBookingError = (e) => {
    // If deposit payment is required and something goes wrong we want to show error box to client
    if (DEPOSIT_ERRORS.includes(e.message)) {
      toast.error(
        "We were unable to charge your card and your appointment has not been booked."
      );
      setClientSecret(null);
      setCreditCardVerified(false);
      setDepositPaymentError(e.message);
      return;
    }

    logError(e);
    if (!hasDjangoMutationError(e)) {
      return toast.error(
        "Can't submit booking. Try reloading the page and if the error continues, please contact us."
      );
    }

    // Rare case, client might have waited too long with booking appointment and someone took this timeslot.
    if (e.message.includes(ClientBookingErrors.BOOKING_ALREADY_EXISTS)) {
      return toast.error(
        "A booking or reservation already exists at the given time. Please contact us"
      );
    }

    // Rare case, after the link had been sent provider created timeblock for this slot
    if (e.message.includes(ClientBookingErrors.PROVIDER_UNAVAILABLE)) {
      return toast.error(
        "Cannot book appointment - provider is unavailable. Please reach out to medspa directly"
      );
    }

    toast.error(e.message);
  };

  const trackPhoneNumberClick = () => {
    analytics.track("Phone Number Clicked in Booking Page", {
      location: "Public Booking Page",
    });
  };

  return {
    medspaData,
    depositsEnabled,
    collectCardInformation,
    showServicePriceOnBooking,
    step,
    setStep,
    selectedServices,
    setSelectedServices,
    selectedProvider,
    setSelectedProvider,
    timeSlot,
    setTimeSlot,
    date,
    setDate,
    clientDetails,
    setClientDetails,
    depositPaymentError,
    setDepositPaymentError,
    accountCreationError,
    setAccountCreationError,
    clientSecret,
    setClientSecret,
    creditCardVerified,
    setCreditCardVerified,
    shouldChooseProvider: eligibleProviders.length > 1,
    providers,
    eligibleProviders,
    handleBookingSubmit,
    card,
    setCard,
    code,
    setCode,
    codeExpired,
    setCodeExpired,
    isReturningClient,
    setIsReturningClient,
    clientAccessToken,
    setClientAccessToken,
    isFds: defaultState.isFds,
    visitId,
    hasMixedAppointmentTypes,
    customizationEnabled: calculateBookingCustomizationEnabled(medspaData),
    onlineBookingDepositsV1Enabled,
    customizationPrimaryColor:
      medspaData.configuration.customizationPrimaryColor,
    customizationTextColor: medspaData.configuration.customizationTextColor,
    cherryIntegrationV1Enabled:
      medspaData.additionalPublicInfo.enabledFeatures.includes(
        FeatureFlags.CHERRY_INTEGRATION_V1
      ),
    cherryPromotionEnabled: medspaData.configuration.cherryPromotionEnabled,
    affirmPromotionEnabled: medspaData.configuration.affirmPromotionEnabled,
    isSelectiveBookingLink,
    selectiveBookingLink,
    packagesSuggestionsV1Enabled:
      medspaData.additionalPublicInfo.enabledFeatures.includes(
        FeatureFlags.PACKAGES_SUGGESTIONS_V1
      ),
    packagesInfo,
    trackPhoneNumberClick,
  };
};

export const ClientBookingContextProvider = ({
  children,
  medspaData,
  defaultState,
  packagesInfo,
  selectiveBookingLinkData,
}: {
  children: ReactNode;
  medspaData: MedspaInfo;
  defaultState: DefaultBookingState;
  packagesInfo: MedspaOnlineStoreData["packages"] | null;
  selectiveBookingLinkData: SelectiveBookingLinkQueryResult["data"] | null;
}) => {
  return (
    <ClientBookingContext.Provider
      value={useClientBookingContextValue(
        medspaData,
        defaultState,
        packagesInfo,
        selectiveBookingLinkData
      )}
    >
      {children}
    </ClientBookingContext.Provider>
  );
};

export const useClientBookingContext = (): ClientBookingContextType => {
  const context = useContext(ClientBookingContext);

  if (!context) {
    throw new Error("Must be used inside a BookingContextProvider");
  }

  return context;
};
